import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import React, { MouseEvent, useEffect, useRef, useState } from 'react'

import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Popover,
  Typography,
} from '@mui/material'

import {
  GridOptions,
  GridReadyEvent,
  RowClickedEvent,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'

import SearchComponent from '@/components/Shared/SearchComponent'
import { optimizationDefaultColDefs } from '@/constants/portfolioOptimization.constant'
import { IBenchmark } from '@/interfaces/portfolioOptimization.interface'
import { getBenchmarks } from '@/services/portfolioOptimization.service'
import { portfolioOptimizationDefaultGridOptions } from '@/utils/grid-utils/colDefs'

type BenchmarkSelectProps = {
  defaultBenchmark?: IBenchmark
  onChange: (benchmark: IBenchmark) => void
}

export const SelectBenchmark: React.FunctionComponent<BenchmarkSelectProps> = ({
  defaultBenchmark,
  onChange,
}) => {
  const [benchmarkList, setBenchmarkList] = useState<IBenchmark[]>([])
  const [selectedBenchmark, setSelectedBenchmark] = useState<
    IBenchmark | undefined
  >(defaultBenchmark)
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const [loading, setLoading] = useState(true)
  const handleClick = (event: MouseEvent<HTMLDivElement | null>) => {
    setAnchorEl(event.currentTarget)
  }
  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const gridRef = useRef<AgGridReact>(null)
  const gridOptions: GridOptions = {
    ...portfolioOptimizationDefaultGridOptions,
    sideBar: false,
    noRowsOverlayComponentParams: () => {
      return {
        noRowsMessage: 'No benchmarks available.',
      }
    },
    rowSelection: 'single' as const,
    columnDefs: [
      {
        ...optimizationDefaultColDefs,
        headerName: 'Benchmark ID',
        field: 'benchmarkId',
        floatingFilter: false,
      },
      {
        ...optimizationDefaultColDefs,
        headerName: 'Benchmark/Portfolio Name',
        field: 'benchmarkName',
        floatingFilter: false,
      },
      {
        ...optimizationDefaultColDefs,
        headerName: 'Type',
        field: 'benchmarkType',
        floatingFilter: false,
      },
      {
        ...optimizationDefaultColDefs,
        headerName: 'Benchmark Family',
        field: 'benchmarkFamily',
        floatingFilter: false,
      },
    ],

    onGridReady: (params: GridReadyEvent) => {
      params.api.sizeColumnsToFit()

      // Highlight selected benchmark when grid is ready
      if (selectedBenchmark && gridRef?.current) {
        gridRef.current.api.forEachNode((node) => {
          if (node?.data.benchmarkId === selectedBenchmark.benchmarkId) {
            node.setSelected(true)
          }
        })
      }
    },
    onRowClicked: (params: RowClickedEvent) => {
      const selectedRow = gridRef.current?.api.getSelectedRows()[0]
      setSelectedBenchmark(selectedRow)
    },
  }

  const handleUseSelectedBenchmark = () => {
    onChange(selectedBenchmark as IBenchmark)
    // close popover
    handlePopoverClose()
  }

  const handleCancel = () => {
    handlePopoverClose()
  }
  const handleSearchChange = (value: string) => {
    // if (gridRef.current) {
    //   gridRef.current.api.setQuickFilter(value)
    // }
  }

  const handleSearchClick = () => {
    // Implement your search logic here
  }

  const togglePopover = Boolean(anchorEl)
  const id = togglePopover ? 'simple-popover' : undefined

  // useEffect(() => {
  //   if (gridRef.current && gridRef.current.api) {
  //     // Update grid options with quick filter text if needed
  //     gridRef.current.api.setQuickFilter('')
  //   }
  // }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getBenchmarks()
        setBenchmarkList(response)
      } catch (err) {
        console.error('Error fetching benchmarks', err)
        setBenchmarkList([])
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  return (
    <Box sx={{ display: 'flex' }}>
      <FormControl
        sx={{ width: '12rem' }}
        variant='outlined'
        aria-describedby={id}
        size='small'
      >
        <InputLabel htmlFor='outlined-adornment-benchmark'>
          Selected Benchmark
        </InputLabel>
        <OutlinedInput
          id='outlined-adornment-benchmark'
          type={'button'}
          value={defaultBenchmark?.benchmarkName ?? 'Select Benchmark'}
          onClick={handleClick}
          className='alignText'
          label='Selected Benchmark'
          endAdornment={
            <InputAdornment position='end'>
              <IconButton aria-label='drop down' edge='end'>
                {togglePopover ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
      <Popover
        id={id}
        open={togglePopover}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Card sx={{ minWidth: '800px' }}>
          <CardContent sx={{ paddingBottom: 0 }}>
            <Typography variant='h3'>
              Choose a Benchmark/Portfolio from the list below
            </Typography>
            <Box display={'flex'} justifyContent={'flex-end'}>
              <SearchComponent
                onSearchChange={handleSearchChange}
                onSearchClick={handleSearchClick}
              />
            </Box>
            <Box className='ag-theme-quartz' marginY={1}>
              <AgGridReact
                ref={gridRef}
                gridOptions={gridOptions}
                rowData={benchmarkList}
                loading={loading}
                data-cy='select-benchmark-ag-grid'
              ></AgGridReact>
            </Box>
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button
              size='small'
              variant='contained'
              color='primary'
              disabled={!Object.keys(selectedBenchmark ?? {}).length}
              onClick={handleUseSelectedBenchmark}
            >
              Use Selection
            </Button>
            <Button size='small' color='primary' onClick={handleCancel}>
              Cancel
            </Button>
          </CardActions>
        </Card>
      </Popover>
    </Box>
  )
}
